import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as userSelectors from 'selectors/user';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import FormSection from 'components/templates/FormSection';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import Errors from 'components/shared/Errors';
import DeleteAccount from 'modules/users/containers/delete-account';
import ConfirmPasswordDialog from 'modules/users/components/confirm-password-dialog';
import ConfirmSaveDialog from 'modules/users/components/confirm-save-dialog';
import BankIdLoginDialog from 'modules/users/components/bankid-login-dialog';
import * as actions from 'actions';
import ProfileTabs from 'modules/users/components/ProfileTabs';
import ProfileForm from 'modules/users/components/ProfileForm';
import { UPDATE_SUCCESS } from 'modules/users/constants';
import SCAVerificationDialog from 'components/shared/SCAVerificationDialog';
import { shouldTriggerSCA } from 'logic/sca';
import useValidateNaturalUserEmail from 'hooks/useValidateNaturalUserEmail';

const styles = makeStyles((theme) => ({
  changepwd: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
    },
  },
}));

const Profile = () => {
  const classes = styles();
  const [panels, setPanels] = React.useState({
    basic: true,
    password: false,
  });
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.userData);
  const canChangeName = useSelector(userSelectors.canChangeName);
  // Company name and type lock on Mangopay KYC, not on canChangeName: BankID
  // verifies the person, not the legal entity. Same predicate the payout form
  // uses (HostInformationForm/Legal.jsx), so the two tabs agree.
  const companyVerified = user.kyc_status === 'SUCCEDED' || user.kyc_status === 'PENDING';
  // Whether a type switch would destroy anything. See onPreSubmit below.
  const hasBankAccount = !!(user.iban || user.bank_account_added_at || user.mango_recipient_id);
  const updateState = useSelector(userSelectors.updateState);
  const success = useSelector(userSelectors.success);
  const submitErrors = useSelector(userSelectors.errors);
  const isImpersonating = useSelector(userSelectors.isImpersonating);
  const [open, setOpen] = React.useState(false);
  const [openBankid, setOpenBankid] = React.useState(false);
  const [openConfirmSave, setOpenConfirmSave] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState(null);
  const [openTypeSwitch, setOpenTypeSwitch] = React.useState(false);
  const [pendingTypeSwitch, setPendingTypeSwitch] = React.useState(null);
  const [switchEmailBlocked, setSwitchEmailBlocked] = React.useState(false);
  const isNaturalUser = user?.type_of_person === 'NATURAL';
  const isCommissionHost = !!user?.booking_active && !!user?.has_reviewed_cottages;
  // Run the check for every commission host, not only those already NATURAL:
  // a LEGAL host switching to private needs the answer before the switch is
  // saved, and this is the only form where that switch happens.
  const shouldValidateEmail = isCommissionHost;
  const emailValidation = useValidateNaturalUserEmail(
    user?.username || user?.email,
    shouldValidateEmail,
  );
  // Only nag a host the rule currently applies to. A company may legitimately
  // use info@; it only becomes a problem if they switch to private.
  const isEmailBlocked =
    shouldValidateEmail && isNaturalUser && (emailValidation.loading || !emailValidation.valid);
  const isEmailValidationLoading = shouldValidateEmail && isNaturalUser && emailValidation.loading;

  const emailValidationMessage = React.useMemo(() => {
    if (emailValidation.valid && !emailValidation.error) {
      return null;
    }

    if (emailValidation.disallowedPrefix) {
      return (
        <FormattedMessage
          id="mangopay.email.invalidPrefix"
          defaultMessage="Email addresses starting with {prefix}@ are not allowed for natural hosts.{br}Please update your email in your profile."
          values={{ prefix: emailValidation.disallowedPrefix, br: <br /> }}
        />
      );
    }

    if (emailValidation.error === 'invalid_format') {
      return (
        <FormattedMessage
          id="mangopay.email.invalidFormat"
          defaultMessage="Please enter a valid email address in your profile to continue."
        />
      );
    }

    return (
      <FormattedMessage
        id="mangopay.email.invalidGeneric"
        defaultMessage="This email address cannot be used for payouts. Please update your email in your profile."
      />
    );
  }, [emailValidation.disallowedPrefix, emailValidation.error, emailValidation.valid]);

  // Refuse the switch only on a definite answer. While the check is in flight,
  // or when it failed for reasons other than the address itself, let the save
  // through rather than block on a network hiccup -- the api enforces the same
  // rule, so a miss here costs a server-side error, not a bad Mango record.
  const emailBlocksNaturalSwitch =
    shouldValidateEmail &&
    !emailValidation.loading &&
    !emailValidation.valid &&
    emailValidation.error !== 'unknown';

  const onSubmit = async (values = submittedData) => {
    const triggerSCA = shouldTriggerSCA(values, user);
    dispatch(actions.updateUserRequest({ ...values, language: user.language, triggerSCA }));
  };

  const profileFormInitialValues = React.useMemo(() => {
    const { language, ...rest } = user;
    return rest;
  }, [user]);

  // Everything the existing save flow does once a type switch is settled.
  const continueSubmit = (values) => {
    // Admin acting as the user: no password / BankID needed, just confirm (STU-17).
    if (isImpersonating) {
      setSubmittedData(values);

      return setOpenConfirmSave(true);
    }

    if (user.type === 'STRICT' && !user.bankid_verified_at) {
      setSubmittedData(values);

      return setOpen(true);
    }

    if (user.type === 'STRICT' && user.bankid_verified_at && !user.bankid_session) {
      setSubmittedData(values);

      return setOpenBankid(true);
    }

    return onSubmit(values);
  };

  // Switching type clears iban, bank_account_added_at, mango_recipient_id and
  // pending_recipient_change (api controllers/users.js) in both directions. The
  // bank account lives on the payout tab, so the host would not see it go --
  // confirm before letting the save through. Only when they actually have one:
  // the IBAN is entered before ID verification, so most hosts reaching this
  // toggle mid-onboarding have nothing to lose.
  const onPreSubmit = (values) => {
    // cleanUpData converts the form's isCompany checkbox into type_of_person
    // and drops the checkbox itself, so compare on type_of_person.
    const switchesType = !!user.type_of_person && values.type_of_person !== user.type_of_person;

    // Going private with a generic address would be saved here and only fail
    // later, on the payout tab, after Mango had already created the natural
    // user. Refuse the switch while the address is one Mango rejects.
    //
    // Unless this same save changes the address: the validation above ran
    // against the stored one, so it says nothing about the new value. Let it
    // through -- the username lands with the switch, and the payout tab (and
    // the api) then judge the address the host actually ends up with.
    const keepsEmail =
      (values.username || '').trim().toLowerCase() === (user.username || '').trim().toLowerCase();

    if (
      switchesType &&
      values.type_of_person === 'NATURAL' &&
      keepsEmail &&
      emailBlocksNaturalSwitch
    ) {
      return setSwitchEmailBlocked(true);
    }

    setSwitchEmailBlocked(false);

    if (switchesType && hasBankAccount) {
      setPendingTypeSwitch(values);

      return setOpenTypeSwitch(true);
    }

    return continueSubmit(values);
  };

  React.useEffect(() => {
    if (user.type === 'SOFT') {
      setPanels({
        basic: true,
        password: true,
      });
    }
  }, [user.type]);

  const [pendingNext, setPendingNext] = React.useState(null);

  React.useEffect(() => {
    if (updateState !== UPDATE_SUCCESS) return;

    setSubmittedData(null);

    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      setPendingNext(next);
    }

    dispatch(actions.resetUserForm());
  }, [updateState, location.search, dispatch]);

  React.useEffect(() => {
    if (pendingNext) {
      history.push(pendingNext);
      setPendingNext(null);
    }
  }, [pendingNext, history]);

  return (
    <Main title={<FormattedMessage id="userform.myProfile" defaultMessage="My profile" />}>
      <ProfileTabs
        tab={0}
        bookingActive={!!(user.has_reviewed_cottages || user.dac7_valid)}
        payoutDisabled={isEmailBlocked}
      />
      {isEmailBlocked && !isEmailValidationLoading && (
        <Box mt={2} display="flex" justifyContent="center">
          <Box width="100%" maxWidth={660}>
            <Alert severity="warning" elevation={3}>
              {emailValidationMessage}
            </Alert>
          </Box>
        </Box>
      )}
      {switchEmailBlocked && emailValidationMessage && (
        <Box mt={2} display="flex" justifyContent="center">
          <Box width="100%" maxWidth={660}>
            <Alert severity="error" elevation={3}>
              {emailValidationMessage}
            </Alert>
          </Box>
        </Box>
      )}
      <BankIdLoginDialog
        open={openBankid}
        header={<FormattedMessage id="bankid.login" defaultMessage="Login with BankID" />}
        title={
          <Box textAlign="center">
            <FormattedMessage
              id="bankid.security"
              defaultMessage="For security reasons, please log in with your BankID before continuing"
            />
          </Box>
        }
        onSuccess={() => {
          setOpenBankid(false);
          onSubmit(submittedData);
        }}
        onClose={() => setOpenBankid(false)}
      />
      <ConfirmPasswordDialog
        onClose={() => setOpen(false)}
        open={open}
        onSuccess={(password) => {
          setOpen(false);

          return onSubmit({
            ...submittedData,
            password,
          });
        }}
      />

      <ConfirmSaveDialog
        open={openTypeSwitch}
        onClose={() => {
          setOpenTypeSwitch(false);
          setPendingTypeSwitch(null);
        }}
        onConfirm={() => {
          const values = pendingTypeSwitch;

          // MUI keeps the button clickable through the fade-out; a second
          // click would otherwise submit an empty body.
          if (!values) return;

          setOpenTypeSwitch(false);
          setPendingTypeSwitch(null);
          continueSubmit(values);
        }}
        titleId="confirm-type-switch-title"
        title={
          <FormattedMessage
            id="profile.confirmTypeSwitch.title"
            defaultMessage="Change account type?"
          />
        }
        body={
          <FormattedMessage
            id="profile.confirmTypeSwitch.body"
            defaultMessage="Switching between private person and company removes your saved bank account. You will need to add it again under Payout."
          />
        }
        confirmLabel={
          <FormattedMessage id="profile.confirmTypeSwitch.confirm" defaultMessage="Change anyway" />
        }
      />

      <ConfirmSaveDialog
        open={openConfirmSave}
        onClose={() => setOpenConfirmSave(false)}
        onConfirm={() => {
          setOpenConfirmSave(false);
          onSubmit(submittedData);
        }}
      />

      <SCAVerificationDialog />

      <FormSection clear>
        <ProfileForm
          canChangeName={canChangeName}
          companyVerified={companyVerified}
          onSubmit={onPreSubmit}
          panels={panels}
          initialValues={profileFormInitialValues}
        />
        <hr />
        <Box py={2} textAlign="right">
          {user.type === 'STRICT' && (
            <Typography variant="body2" component="span" className={classes.changepwd}>
              <Link to="/change-password">
                <FormattedMessage id="userform.changePassword" defaultMessage="Change password" />
              </Link>
            </Typography>
          )}
          &nbsp;&nbsp;&nbsp;
          <DeleteAccount />
        </Box>
      </FormSection>

      <Errors errors={submitErrors} />
      <Success success={success} />
    </Main>
  );
};

export default Profile;
